import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

/**
 * Client Care config hook — reads + writes companies/{c}/client_care_config/default
 * via the admin-gated getClientCareConfig / updateClientCareConfig callables
 * (RBAC enforced server-side: super_admin + administrator only).
 *
 * Mirrors the httpsCallable pattern in useClientDetail. The config shape is the
 * server's ClientCareConfigSchema; we keep it structurally typed here (the page
 * narrows the bits it edits) rather than re-deriving the Zod type in the bundle.
 */

export interface CadenceRule {
  enabled: boolean;
  intervalDays?: number;
  /** Month-based timing: settlement check-in offset / periodic-review frequency. */
  intervalMonths?: number;
  /** Week-based timing (e.g. a 6-week settlement check-in). Beats months. */
  intervalWeeks?: number;
  leadDays?: number;
  escalationDays?: number;
}

/** Per-broker TIMING override (admin-managed); unset fields inherit the company default. */
export interface BrokerCadenceOverride {
  intervalMonths?: number;
  intervalWeeks?: number;
  intervalDays?: number;
  leadDays?: number;
  escalationDays?: number;
}

export interface ChannelPrefs {
  email: boolean;
  chat: boolean;
}

export interface CustomEventType {
  typeId: string;
  label: string;
  anchorMode: 'date_anchored' | 'interval';
  intervalDays?: number;
  leadDays?: number;
  escalationDays?: number;
  enabled: boolean;
}

export interface NotificationPrefs {
  default: ChannelPrefs;
  byRole: Record<string, ChannelPrefs>;
  googleChatWebhookByOffice: Record<string, string>;
}

export type EscalationRouting =
  | { mode: 'role_based' }
  | { mode: 'selected_recipients'; recipientUids: string[] };

export interface ClientCareConfig {
  cadences: Record<string, CadenceRule>;
  /** Per-broker timing overrides: brokerDocId -> eventType -> override. Admin-managed. */
  cadencesByBroker?: Record<string, Record<string, BrokerCadenceOverride>>;
  customEventTypes: CustomEventType[];
  notifications: NotificationPrefs;
  escalationRouting: EscalationRouting;
  promptSendHour: number;
  updatedAt?: unknown;
  updatedBy?: string;
}

interface GetConfigResponse {
  config: ClientCareConfig;
  exists: boolean;
}

interface UpdateConfigResponse {
  ok: boolean;
  config: ClientCareConfig;
}

export function useClientCareConfig(companyId?: string) {
  const [config, setConfig] = useState<ClientCareConfig | null>(null);
  const [exists, setExists] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!companyId) {
      setConfig(null);
      setExists(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable<{ companyId: string }, GetConfigResponse>(
        functions,
        'getClientCareConfig'
      );
      const res = await fn({ companyId });
      setConfig(res.data.config ?? null);
      setExists(!!res.data.exists);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load client care config');
      console.error('[useClientCareConfig] load failed:', e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  /** Persist the full config. Returns true on success (page shows toast). */
  const save = useCallback(
    async (next: ClientCareConfig): Promise<boolean> => {
      if (!companyId) return false;
      setSaving(true);
      setError(null);
      try {
        const fn = httpsCallable<
          { companyId: string; config: ClientCareConfig },
          UpdateConfigResponse
        >(functions, 'updateClientCareConfig');
        const res = await fn({ companyId, config: next });
        setConfig(res.data.config ?? next);
        setExists(true);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to save client care config');
        console.error('[useClientCareConfig] save failed:', e);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [companyId]
  );

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { config, exists, loading, saving, error, refetch, save };
}
